// Library methods
import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAuth0 } from "@auth0/auth0-react";

// MUI Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

// Components
import {
  ChecklistIcon,
  FastForwardIcon,
  PauseIcon,
  PlayIcon,
  RestartIcon,
  TrashIcon,
  VRIcon,
} from "../../../../assets/icons";

// Utilities
import useClinic from "../../../../hooks/useClinic";
import { DeviceStagesTimeline } from "../../../../utils/signalRHelper";
import { useLiveUpdateStatusContext } from "../../../../contexts/LiveUpdateStatusContext";
import {
  updateDeviceStage,
  deviceExamTerminationRequest,
} from "../../../../services/SignalR";
import { examCardEnums } from "../../../../enums";

const buttonStyle = {
  color: "#FFF",
  borderRadius: "4px",
  borderColor: "none",
  backgroundColor: "#5B6D7A",
  "&:hover": {
    backgroundColor: "#475966",
  },
  "&:disabled": {
    color: "#FFF",
    backgroundColor: "#5B6D7A43",
  },
  width: 48,
  height: 40,
};

const LiveUpdateControls = ({ isLoadingSignalR, connection }) => {
  // translations
  const { t } = useTranslation();

  // auth
  const { getAccessTokenSilently } = useAuth0();

  // clinic context
  const { clinicLicensing } = useClinic();

  const {
    exam,
    device,
    isVrMode,
    isPaused,
    isConfirmation,
    inTutorial,
    bothControllersDisconnected,
    hmdInProximity,
    activeStep,
    actions,
  } = useLiveUpdateStatusContext();

  const { setExamReportLive, setIsVrMode } = actions;

  const disableRestartTermination = useMemo(
    () =>
      (activeStep.value !== DeviceStagesTimeline.Exam &&
        activeStep.value !== DeviceStagesTimeline.Tutorial) ||
      isLoadingSignalR,
    [activeStep, isLoadingSignalR]
  );

  // handle the update of a device's stage
  const handleUpdateDeviceStage = useCallback(
    async (newStage) => {
      try {
        const token = await getAccessTokenSilently();
        const response = await updateDeviceStage(
          token,
          device?.id,
          newStage,
          connection
        );
        const data = JSON.parse(response);
        if (data?.state === true && newStage === "Confirmation") {
          setExamReportLive(undefined);
        }
      } catch (e) {}
    },
    [device?.id, connection, setExamReportLive, getAccessTokenSilently]
  );

  const handleDeviceExamTerminationRequest = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      await deviceExamTerminationRequest(token, device?.id, connection);
    } catch (e) {
      console.error("Could not terminate", e.message);
    }
  }, [connection, device?.id, getAccessTokenSilently]);

  const handleVRModeSwitch = useCallback(async () => {
    setIsVrMode((prev) => !prev);
  }, [setIsVrMode]);

  const leftExamCanStart = useMemo(() => {
    if (!exam) return false;

    // get the current exam section being tested
    const currentExamType = exam?.nextSection?.type;
    const enumValues = Object.values(examCardEnums);
    const currentSectionName = enumValues.find(
      (value) => value.type === currentExamType
    )?.sectionName;

    const sections = exam?.[currentSectionName];

    return (
      sections?.length > 1 &&
      sections[0].completionDate === null &&
      device?.deviceStatus?.stage?.stageType === "Congratulations"
    );
  }, [exam, device]);

  const isPlayMode = useMemo(
    () => isPaused || isConfirmation || leftExamCanStart,
    [isPaused, isConfirmation, leftExamCanStart]
  );

  const disablePlayPauseButton = useMemo(
    () =>
      (activeStep.value !== DeviceStagesTimeline.Exam &&
        activeStep.value !== DeviceStagesTimeline.Confirmation &&
        !inTutorial &&
        !leftExamCanStart) ||
      isLoadingSignalR ||
      ((bothControllersDisconnected || !hmdInProximity) &&
        (isPaused || isConfirmation || leftExamCanStart)),
    [
      activeStep,
      inTutorial,
      leftExamCanStart,
      bothControllersDisconnected,
      hmdInProximity,
      isConfirmation,
      isPaused,
      isLoadingSignalR,
    ]
  );

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      width="100%"
      gap="60px"
      sx={{ opacity: isLoadingSignalR ? 0.4 : 1 }}
    >
      <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
        <Tooltip
          title={isPlayMode ? t("play_tooltip") : t("pause_tooltip")}
          disableHoverListener={disablePlayPauseButton}
        >
          <span>
            <IconButton
              aria-label="pause and resume button"
              sx={{ ...buttonStyle }}
              disabled={disablePlayPauseButton}
              onClick={() =>
                handleUpdateDeviceStage(
                  isPaused && inTutorial
                    ? "Tutorial"
                    : isPlayMode
                      ? "Exam"
                      : "Pause"
                )
              }
            >
              {isPlayMode ? <PlayIcon /> : <PauseIcon />}
            </IconButton>
          </span>
        </Tooltip>
        <Typography noWrap component="div" variant="body2">
          {isPlayMode ? t("word_play") : t("word_pause")}
        </Typography>
      </Box>
      {/* Restart button */}
      <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
        <Tooltip
          title={t("restart_tooltip")}
          disableHoverListener={disableRestartTermination}
        >
          <span>
            <IconButton
              aria-label="restart button"
              sx={{ ...buttonStyle }}
              disabled={disableRestartTermination}
              onClick={() => handleUpdateDeviceStage("Confirmation")} // Restart the exam
            >
              <RestartIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Typography noWrap component="div" variant="body2">
          {t("word_restart")}
        </Typography>
      </Box>
      {/* Terminate button */}
      <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
        <Tooltip
          title={t("terminate_tooltip")}
          disableHoverListener={disableRestartTermination}
        >
          <span>
            <IconButton
              aria-label="terminate button"
              sx={{ ...buttonStyle }}
              disabled={disableRestartTermination}
              onClick={handleDeviceExamTerminationRequest} // Terminate the exam
            >
              <TrashIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Typography noWrap component="div" variant="body2">
          {t("word_terminate")}
        </Typography>
      </Box>
      {/* Skip tutorial */}
      {inTutorial && !isLoadingSignalR && (
        <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
          <Tooltip
            title={t("skip_tutorial_tooltip")}
            disableHoverListener={disableRestartTermination}
          >
            <span>
              <IconButton
                aria-label="skip tutorial button"
                sx={{ ...buttonStyle }}
                onClick={() => handleUpdateDeviceStage("Exam")} // Skip the tutorial
              >
                <FastForwardIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Typography noWrap component="div" variant="body2">
            {t("word_skip")}
          </Typography>
        </Box>
      )}
      {/* VR Mode / Exam Mode button */}
      {clinicLicensing?.canVideoStream && (
        <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
          <Tooltip
            title={
              !isVrMode
                ? t("word_switch_to_vr_view")
                : t("word_switch_to_exam_view")
            }
          >
            <span>
              <IconButton
                aria-label={!isVrMode ? "vr mode button" : "exam mode button"}
                sx={{ ...buttonStyle }}
                onClick={handleVRModeSwitch} // switches the view mode
              >
                {!isVrMode ? <VRIcon /> : <ChecklistIcon />}
              </IconButton>
            </span>
          </Tooltip>
          <Typography noWrap component="div" variant="body2">
            {!isVrMode ? t("word_vr_mode") : t("word_exam_mode")}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default LiveUpdateControls;
